import * as PIXI from 'pixi.js-legacy';
import Base from './Base';
import constants from '../settings/constants';
import { ITEMS } from '../constants';
import productService from '../services/products';

export default class DropArea extends Base {
  constructor(options) {
    super(options);
    this.ratio = options.ratio;
    this.slot = options.slot;
    this.shouldRenderFullSize = options.fullSize;
    this.draw(options.width, options.height);
  }

  clampedHeight(height) {
    const max =
      Math.min(
        constants.DROP_AREA_MAX_HEIGHT,
        constants.DISTANCE_BETWEEN_ATTACHMENTS *
          (1 - constants.DROP_AREA_SEPARATION)
      ) * this.ratio;
    const min = constants.DROP_AREA_MIN_HEIGHT * this.ratio;
    return Math.max(min, Math.min(max, height));
  }
  /**
   * Gets the the information about the dimensions of the drop area to be drawn.
   * Also includes an attribute that helps with adjusting the width of the drop area in relation to
   * how much the drop area overlaps with the posts. In the case for tables it also considers the possible
   * overlap between drop areas that are next to each other horizontally.
   * @param {Object} slot
   * @param {number} wantedHeight
   * @returns {Object} An object that contains information about the dimensions of the drop area to be drawn as well as how much to adjust the width.
   */
  getSize(slot, wantedHeight) {
    const postWidth = productService.getPostWidth();

    if (this.shouldRenderFullSize) {
      return {
        x: 0,
        y: 0,
        widthToSubtract: 0,
        height: wantedHeight,
      };
    }
    switch (slot.filter.type) {
      case 'cover':
        return {
          x: Math.floor(postWidth * this.ratio),
          y: (this.slot.height - this.slot.parent.height) * this.ratio,
          widthToSubtract: Math.floor(2 * postWidth * this.ratio),
          height: this.slot.parent.height * this.ratio,
        };
      default:
        return {
          x: Math.floor(postWidth * this.ratio),
          y: 0,
          widthToSubtract: !slot.horizontalOverlap
            ? Math.floor(2 * postWidth * this.ratio)
            : slot.horizontalOverlap * this.ratio,
          height: this.clampedHeight(wantedHeight),
        };
    }
  }

  draw(width, wantedHeight) {
    const size = this.getSize(this.slot, wantedHeight);

    const height = size.height;

    width -= size.widthToSubtract;
    const back = new PIXI.Graphics()
      .beginFill(0xfafafa)
      .drawRect(0, 0, width, height)
      .endFill();

    back.alpha = 0.5;
    back.x = size.x;
    back.y = size.y;

    this.addChild(back);

    const arrowMaxHeight = this.shouldRenderFullSize ? Infinity : 13;
    // UX design says arraw needs 1px margin
    const arrowHeight = Math.min(height, arrowMaxHeight);
    const arrowWidth = arrowHeight / 2;
    const arrowY = (height - arrowHeight) / 2;

    const buffer = 1;

    const leftArrow = new PIXI.Graphics()
      .beginFill(0x407ab1)
      .drawPolygon(
        0,
        0,
        0,
        arrowHeight,
        buffer,
        arrowHeight,
        buffer + arrowWidth,
        arrowHeight / 2,
        buffer,
        0
      )
      .endFill();

    leftArrow.x = back.x;
    leftArrow.y = arrowY;

    this.addChild(leftArrow);

    if (this.hasRightArrow(this.slot)) {
      const rightArrow = leftArrow.clone();

      rightArrow.pivot = new PIXI.Point(rightArrow.width, rightArrow.height);
      rightArrow.rotation = Math.PI;
      rightArrow.x = back.x + width - rightArrow.width;
      rightArrow.y = arrowY;

      this.addChild(rightArrow);
    }
  }

  hasRightArrow(slot) {
    return slot.filter.type !== ITEMS.TABLE;
  }
}
